import { registerOverlay } from 'klinecharts'
import { CHART_FONT_FAMILY } from './chartColors'
import { resolvePricePrecision, type OverlayFigure } from './overlayUtils'
import { NO_DEFAULT_FIGURES, type OverlayTemplateParam } from './registrationUtils'

/**
 * Measure / ruler tool — a two-click box drawn as an L-frame from the drag-start
 * corner:
 *   • a DASHED vertical arrow up/down the price move (left edge)
 *   • a DASHED horizontal arrow along the time move (bottom edge)
 *   • arrowheads at the far ends so the direction reads at a glance
 *   • NO top / right border (an open L, not a closed box) over a faint fill
 *   • a hollow start dot + a filled end dot
 * plus two outlined pills (matching the chart's chrome):
 *   • right  "+45.98%" / "+$2.38"          — coloured, just past the right edge,
 *                                             75% of the way toward the end cursor
 *   • bottom "26 bars · 36d" / "Vol: 83.50M" — neutral, centred below the range
 * Up = green, down = red (fill, arrows, dots, and the change values); the span /
 * volume lines stay neutral chrome.
 *
 * Math + figure construction are pure (unit-tested). `registerMeasureOverlay`
 * wires them into a klinecharts overlay template; volume can't be reached from
 * `createPointFigures`, so each ChartManager registers a per-chart resolver
 * (see registerMeasureVolumeResolver) that sums its bars' volume in range.
 *
 * The overlay is EPHEMERAL: ChartManager skips persistence and removes it on the
 * next click / Escape (see ChartManager.handleOverlayDrawEnd).
 */

// Concrete colors (overlays paint to canvas, so they can't read CSS vars).
export const MEASURE_UP_FILL = 'rgba(38, 166, 154, 0.12)'
export const MEASURE_DOWN_FILL = 'rgba(239, 83, 80, 0.12)'
const MEASURE_UP_SOLID = 'rgb(38, 166, 154)' // arrows + dots + change values
const MEASURE_DOWN_SOLID = 'rgb(239, 83, 80)'

// Faint 50%-of-range guide line — a soft slate hairline at the vertical
// midpoint of the drag box. Present enough to read, quiet enough to ignore.
const MEASURE_MIDLINE = 'rgba(148, 163, 184, 0.45)'

// Neutral pill chrome — dark translucent fill + hairline border + light text,
// matching the "26 bars" chip in the design (no filled accent colour).
const PILL_BG = 'rgba(20, 24, 33, 0.9)'
const PILL_BORDER = 'rgba(148, 163, 184, 0.35)'
const PILL_TEXT = '#e2e8f0'

export interface MeasurePoint {
  value: number
  timestamp: number
  dataIndex: number
}

export interface MeasureStats {
  deltaPrice: number
  deltaPct: number
  elapsedMs: number
  bars: number
  up: boolean
}

type Coord = { x: number; y: number }

type Figure = OverlayFigure

// ─── Per-chart volume resolver ───────────────────────────────────────────────
// createPointFigures has no access to the chart's bars, so each ChartManager
// registers a resolver bound to its data list. The first one that recognises the
// overlay id (i.e. owns it) returns the summed volume; others return null.
type MeasureVolumeResolver = (
  overlayId: string,
  fromIndex: number,
  toIndex: number,
) => number | null
const volumeResolvers = new Set<MeasureVolumeResolver>()

export function registerMeasureVolumeResolver(fn: MeasureVolumeResolver): () => void {
  volumeResolvers.add(fn)
  return () => volumeResolvers.delete(fn)
}

function resolveMeasureVolume(overlayId: string, from: number, to: number): number | null {
  for (const fn of volumeResolvers) {
    const v = fn(overlayId, from, to)
    if (v != null) return v
  }
  return null
}

// ─── Pure math / formatting ──────────────────────────────────────────────────

/** Derive the readout numbers from the two measured points. */
export function computeMeasure(p0: MeasurePoint, p1: MeasurePoint): MeasureStats {
  const deltaPrice = p1.value - p0.value
  const deltaPct = p0.value !== 0 ? (deltaPrice / p0.value) * 100 : 0
  return {
    deltaPrice,
    deltaPct,
    elapsedMs: Math.abs(p1.timestamp - p0.timestamp),
    bars: Math.abs(p1.dataIndex - p0.dataIndex),
    up: deltaPrice >= 0,
  }
}

/** Compact duration: the two most significant non-zero units (e.g. "1h 12m"). */
export function formatDuration(ms: number): string {
  const totalMin = Math.round(ms / 60_000)
  const d = Math.floor(totalMin / 1440)
  const h = Math.floor((totalMin % 1440) / 60)
  const m = totalMin % 60
  if (d > 0) return h > 0 ? `${d}d ${h}h` : `${d}d`
  if (h > 0) return m > 0 ? `${h}h ${m}m` : `${h}h`
  return `${m}m`
}

/** Compact volume with K/M/B/T suffix (e.g. 5_270_000 → "5.27M"). */
export function formatVolume(v: number): string {
  const abs = Math.abs(v)
  if (abs >= 1e12) return `${(v / 1e12).toFixed(2)}T`
  if (abs >= 1e9) return `${(v / 1e9).toFixed(2)}B`
  if (abs >= 1e6) return `${(v / 1e6).toFixed(2)}M`
  if (abs >= 1e3) return `${(v / 1e3).toFixed(2)}K`
  return `${Math.round(v)}`
}

function fmtSignedPrice(delta: number, precision: number): string {
  return `${delta >= 0 ? '+' : '-'}$${Math.abs(delta).toFixed(precision)}`
}

function fmtSignedPct(pct: number): string {
  return `${pct >= 0 ? '+' : '-'}${Math.abs(pct).toFixed(2)}%`
}

// ─── Figure helpers ──────────────────────────────────────────────────────────

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v))

function rectFig(x: number, y: number, w: number, h: number, color: string): Figure {
  return {
    type: 'rect',
    ignoreEvent: true,
    attrs: { x, y, width: w, height: h },
    styles: { style: 'fill', color, borderRadius: 0 },
  }
}

/** A neutral outlined pill box (dark fill + hairline border, rounded). */
function pillRect(x: number, y: number, w: number, h: number): Figure {
  return {
    type: 'rect',
    ignoreEvent: true,
    attrs: { x, y, width: w, height: h },
    styles: {
      style: 'stroke_fill',
      color: PILL_BG,
      borderColor: PILL_BORDER,
      borderSize: 1,
      borderRadius: 6,
    },
  }
}

function lineFig(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  color: string,
  size = 1.5,
  dashed = false,
): Figure {
  return {
    type: 'line',
    ignoreEvent: true,
    attrs: {
      coordinates: [
        { x: x1, y: y1 },
        { x: x2, y: y2 },
      ],
    },
    styles: dashed ? { color, size, style: 'dashed', dashedValue: [4, 4] } : { color, size },
  }
}

function circleFig(x: number, y: number, r: number, color: string, filled: boolean): Figure {
  return {
    type: 'circle',
    ignoreEvent: true,
    attrs: { x, y, r },
    styles: filled
      ? { style: 'fill', color }
      : { style: 'stroke_fill', color: PILL_BG, borderColor: color, borderSize: 1.5 },
  }
}

/** Plain centred text (transparent) — used for the coloured lines inside a pill. */
function textFig(
  x: number,
  y: number,
  text: string,
  color: string,
  size = 11,
  weight: 'normal' | 'bold' = 'normal',
): Figure {
  return {
    type: 'text',
    ignoreEvent: true,
    attrs: { x, y, text, align: 'center', baseline: 'middle' },
    styles: {
      style: 'fill',
      color,
      size,
      weight,
      family: CHART_FONT_FAMILY,
      backgroundColor: 'transparent',
      paddingLeft: 0,
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
    },
  }
}

/** One line inside a stacked pill: its text, font size, colour, and weight. */
interface PillLine {
  text: string
  size: number
  color: string
  weight?: 'normal' | 'bold'
}

const PILL_CHAR_W = 8
const PILL_PAD_X = 10
const PILL_PAD_Y = 8
const PILL_LINE_GAP = 4

/** Box footprint (w, h) a stacked pill needs for `lines`, so callers can anchor
 * it (centre / right edge) before drawing. */
function measurePill(lines: PillLine[]): { w: number; h: number } {
  const w = Math.max(...lines.map((l) => l.text.length)) * PILL_CHAR_W + PILL_PAD_X * 2
  const h =
    lines.reduce((s, l) => s + l.size, 0) + PILL_LINE_GAP * (lines.length - 1) + PILL_PAD_Y * 2
  return { w, h }
}

/**
 * A neutral outlined pill whose top-left is (boxX, boxY), stacking `lines`
 * vertically and centring each horizontally. The bordered `pillRect` owns the
 * outline (text figures render only a background); each line keeps its own
 * colour/size/weight so a coloured change value and neutral chrome can share a
 * box.
 */
function stackedPill(boxX: number, boxY: number, lines: PillLine[]): Figure[] {
  const { w, h } = measurePill(lines)
  const figs: Figure[] = [pillRect(boxX, boxY, w, h)]
  let ly = boxY + PILL_PAD_Y
  for (const line of lines) {
    figs.push(
      textFig(boxX + w / 2, ly + line.size / 2, line.text, line.color, line.size, line.weight),
    )
    ly += line.size + PILL_LINE_GAP
  }
  return figs
}

/**
 * Two solid barbs at a tip, pointing along the unit vector (ux, uy). A degenerate
 * (zero-length) direction yields no barbs.
 */
function arrowHead(tipX: number, tipY: number, ux: number, uy: number, color: string): Figure[] {
  if (ux === 0 && uy === 0) return []
  const px = -uy // perpendicular
  const py = ux
  const head = 7
  const spread = 4
  return [
    lineFig(tipX, tipY, tipX - ux * head + px * spread, tipY - uy * head + py * spread, color),
    lineFig(tipX, tipY, tipX - ux * head - px * spread, tipY - uy * head - py * spread, color),
  ]
}

/** The faint fill, the L-frame + direction arrows, the endpoint dots, and the
 * three neutral pills. */
export function buildMeasureFigures(
  c0: Coord,
  c1: Coord,
  stats: MeasureStats,
  pricePrecision: number,
  volume: number | null,
  /**
   * The pane's drawable size. When given, the two pills are kept ON-SCREEN:
   * the right change-pill flips to the INSIDE of the box's right edge when it
   * would overflow the right side (and both pills clamp to the bounds). Omit to
   * keep the raw outside-the-box placement (used by the pure-math unit tests).
   */
  bounds?: { width: number; height: number },
): Figure[] {
  const left = Math.min(c0.x, c1.x)
  const top = Math.min(c0.y, c1.y)
  const bottom = Math.max(c0.y, c1.y)
  const width = Math.max(Math.abs(c1.x - c0.x), 1)
  const height = Math.max(Math.abs(c1.y - c0.y), 1)
  const right = Math.max(c0.x, c1.x)
  const fill = stats.up ? MEASURE_UP_FILL : MEASURE_DOWN_FILL
  const solid = stats.up ? MEASURE_UP_SOLID : MEASURE_DOWN_SOLID
  const midX = (c0.x + c1.x) / 2

  const figures: Figure[] = [rectFig(left, top, width, height, fill)]

  // L-frame emanating from the START corner (c0), each dashed line capped with
  // an arrowhead at the far end so the drag direction reads at a glance.
  // Vertical = the price leg (left edge for the canonical up-right drag).
  if (c1.y !== c0.y) {
    figures.push(lineFig(c0.x, c0.y, c0.x, c1.y, solid, 1.5, true))
    figures.push(...arrowHead(c0.x, c1.y, 0, Math.sign(c1.y - c0.y), solid))
  }
  // Horizontal = the time leg (bottom edge for the canonical up-right drag).
  if (c1.x !== c0.x) {
    figures.push(lineFig(c0.x, c0.y, c1.x, c0.y, solid, 1.5, true))
    figures.push(...arrowHead(c1.x, c0.y, Math.sign(c1.x - c0.x), 0, solid))
  }

  // Endpoint dots: hollow at the start, filled at the end.
  figures.push(circleFig(c0.x, c0.y, 4, solid, false))
  figures.push(circleFig(c1.x, c1.y, 4, solid, true))

  // Soft 50% guide — a thin dashed hairline across the vertical midpoint of the
  // range, marking the halfway price. Subtle by design (low-alpha slate).
  if (c1.y !== c0.y) {
    const midY = (c0.y + c1.y) / 2
    figures.push(lineFig(left, midY, right, midY, MEASURE_MIDLINE, 1, true))
  }

  // ── Right label — % + price change, a coloured pill just outside the RIGHT
  // edge, anchored 75% of the way from the start toward the END cursor (so it
  // sits by the endpoint rather than the middle) and out of the drag's way.
  const changeLines: PillLine[] = [
    { text: fmtSignedPct(stats.deltaPct), size: 14, color: solid, weight: 'bold' },
    { text: fmtSignedPrice(stats.deltaPrice, pricePrecision), size: 11, color: solid },
  ]
  const { w: changeW, h: changeH } = measurePill(changeLines)
  const changeCenterY = c0.y + 0.75 * (c1.y - c0.y)
  let changeX = right + 12
  let changeY = changeCenterY - changeH / 2
  if (bounds) {
    const M = 4
    // Overflows the right edge → flip to just INSIDE the box's right edge.
    if (changeX + changeW > bounds.width - M) changeX = right - 12 - changeW
    changeX = clamp(changeX, M, Math.max(M, bounds.width - changeW - M))
    changeY = clamp(changeY, M, Math.max(M, bounds.height - changeH - M))
  }
  figures.push(...stackedPill(changeX, changeY, changeLines))

  // ── Bottom label — bars · time, with volume on a second line, one box
  // centred BELOW the range. Both lines are neutral chrome (no accent colour).
  const spanLines: PillLine[] = [
    { text: `${stats.bars} bars · ${formatDuration(stats.elapsedMs)}`, size: 11, color: PILL_TEXT },
  ]
  if (volume != null) {
    spanLines.push({ text: `Vol: ${formatVolume(volume)}`, size: 11, color: PILL_TEXT })
  }
  const { w: spanW, h: spanH } = measurePill(spanLines)
  let spanX = midX - spanW / 2
  let spanY = bottom + 12
  if (bounds) {
    const M = 4
    // Overflows the bottom → flip ABOVE the range instead.
    if (spanY + spanH > bounds.height - M) spanY = top - 12 - spanH
    spanX = clamp(spanX, M, Math.max(M, bounds.width - spanW - M))
    spanY = clamp(spanY, M, Math.max(M, bounds.height - spanH - M))
  }
  figures.push(...stackedPill(spanX, spanY, spanLines))

  return figures
}

/** Register the `measure` overlay template with klinecharts (once, at module load). */
export function registerMeasureOverlay(): void {
  registerOverlay({
    name: 'measure',
    totalStep: 3, // two clicks (points + 1)
    ...NO_DEFAULT_FIGURES, // transient: no draggable anchor handles
    createPointFigures: ({ coordinates, overlay, precision, yAxis, bounding }) => {
      if (coordinates.length < 2) return []
      const pts = overlay.points as Array<{
        value?: number
        timestamp?: number
        dataIndex?: number
      }>
      const p0 = pts[0]
      const p1 = pts[1]
      if (!p0 || !p1 || p0.value == null || p1.value == null) return []
      const stats = computeMeasure(
        { value: p0.value, timestamp: p0.timestamp ?? 0, dataIndex: p0.dataIndex ?? 0 },
        { value: p1.value, timestamp: p1.timestamp ?? 0, dataIndex: p1.dataIndex ?? 0 },
      )
      const pricePrecision = resolvePricePrecision(yAxis, precision)
      const volume = resolveMeasureVolume(overlay.id, p0.dataIndex ?? 0, p1.dataIndex ?? 0)
      const bounds =
        bounding && typeof bounding.width === 'number' && typeof bounding.height === 'number'
          ? { width: bounding.width, height: bounding.height }
          : undefined
      return buildMeasureFigures(
        coordinates[0] as Coord,
        coordinates[1] as Coord,
        stats,
        pricePrecision,
        volume,
        bounds,
      )
    },
  } as OverlayTemplateParam)
}
